'use client';

import { useRouter } from 'next/navigation';
import { useParams } from 'next/navigation';
import { Locales } from '@/types/i18n';
import StampLogo from '@/components/ui/logo/StampLogo';
import styles from './SectionSwitcher.module.css';
import { withMarketQuery } from '@/lib/utils/withMarketQuery';

// Import translations
import enTranslation from '@/locales/en/translation.json';
import esTranslation from '@/locales/es/translation.json';
import deTranslation from '@/locales/de/translation.json';
import itTranslation from '@/locales/it/translation.json';
import zhCNTranslation from '@/locales/zh_CN/translation.json';

// Define a type for nested translation objects
type NestedTranslation = {
  [key: string]: string | NestedTranslation;
};

// Helper function to get translation based on locale
const getTranslation = (locale: Locales, key: string): string => {
  const keys = key.split('.');
  let translationObj: NestedTranslation;

  switch (locale) {
    case 'en':
      translationObj = enTranslation;
      break;
    case 'es':
      translationObj = esTranslation;
      break;
    case 'de':
      translationObj = deTranslation;
      break;
    case 'it':
      translationObj = itTranslation;
      break;
    case 'zh-CN':
      translationObj = zhCNTranslation;
      break;
    default:
      translationObj = enTranslation;
  }

  // Navigate through the nested keys
  let result: string | NestedTranslation = translationObj;
  for (const k of keys) {
    if (result && typeof result === 'object' && k in result) {
      result = result[k];
    } else {
      return key; // Return the key if translation not found
    }
  }

  return typeof result === 'string' ? result : key;
};

interface SectionSwitcherProps {
  isMerchantsPage: boolean;
  onMenuClose?: () => void;
}

export default function SectionSwitcher({ isMerchantsPage, onMenuClose }: SectionSwitcherProps) {
  const router = useRouter();
  const params = useParams();
  const locale = (params.locale as Locales) || 'en';

  // Switch section and preserve ?market param
  const handleSwitchSection = (section: 'travelers' | 'merchants') => {
    if (section === 'travelers') {
      router.push(withMarketQuery(`/${locale}/travelers`));
    } else {
      router.push(withMarketQuery(`/${locale}/merchants`));
    }
    if (onMenuClose) {
      onMenuClose();
    }
  };

  return (
    <div className={styles.centerSection}>
      <div
        className={`${styles.switcherWrapper} ${isMerchantsPage ? styles.merchantsActive : styles.travelersActive}`}
      >
        <div className={styles.switcherSlider}></div>
        <div 
          className={`${styles.switcherButton} ${isMerchantsPage ? styles.active : ''}`}
          onClick={() => handleSwitchSection('merchants')}
        >
          <StampLogo className={styles.switcherLogo} />{' '}
          {getTranslation(locale, 'navbar.forMerchants')}
        </div>
        <div 
          className={`${styles.switcherButton} ${!isMerchantsPage ? styles.active : ''}`}
          onClick={() => handleSwitchSection('travelers')}
        >
          <StampLogo className={styles.switcherLogo} />{' '}
          {getTranslation(locale, 'navbar.forTravelers')}
        </div>
      </div>
    </div>
  );
}
